import { Card, Flex, Typography } from "antd";
import { CheckIcon, ClockIcon, XIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { colors } from "@libertify/ui/theme-v2";
import type { ExperienceInsight } from "../../types";
import { InsightActionButtons } from "./InsightActionButtons";
import { InsightCategoryTag } from "./InsightCategoryTag";
import { useMemo } from "react";

export type InsightCardProps = {
  insight: ExperienceInsight;
  onClick: (insight: ExperienceInsight) => void;
  onComplete?: (id: string) => void;
  onPostpone?: (id: string) => void;
  onDismiss?: (id: string) => void;
  onReport?: (id: string) => void;
};

type TextProps = React.ComponentProps<typeof Typography.Text>;

export const InsightCard = ({
  insight,
  onClick,
  onComplete,
  onPostpone,
  onDismiss,
  onReport,
}: InsightCardProps) => {
  const t = useTranslate();
  const isCompleted = insight.task?.state === "Done";
  const isDismissed = insight.task?.state === "Dismissed";
  const isPostponed = insight.task?.state === "Snoozed";

  const backgroundColor = useMemo(() => {
    if (isCompleted) return colors.status.successBg;
    if (isPostponed) return colors.status.warningBg;
    return undefined;
  }, [isCompleted, isPostponed]);

  const label:
    | {
        type: TextProps["type"];
        text: "completed" | "postponed" | "dismissed";
        icon: React.ReactNode;
      }
    | undefined = useMemo(() => {
    if (isCompleted)
      return {
        type: "success",
        text: "completed",
        icon: <CheckIcon size={14} weight="bold" style={{ marginRight: 4 }} />,
      };
    if (isPostponed)
      return {
        type: "warning",
        text: "postponed",
        icon: <ClockIcon size={14} weight="bold" style={{ marginRight: 4 }} />,
      };
    if (isDismissed)
      return {
        type: "danger",
        text: "dismissed",
        icon: <XIcon size={14} weight="bold" style={{ marginRight: 4 }} />,
      };
  }, [isCompleted, isPostponed, isDismissed]);

  return (
    <Card
      hoverable
      style={{
        cursor: "pointer",
        opacity: isDismissed ? 0.5 : 1,
        backgroundColor,
      }}
      styles={{ body: { padding: 16 } }}
      onClick={() => onClick(insight)}
    >
      <Flex vertical gap={12}>
        <Typography.Title
          level={3}
          style={{
            margin: 0,
            textDecoration: isCompleted ? "line-through" : undefined,
          }}
        >
          {insight.action}
        </Typography.Title>
        <Typography.Paragraph
          type="secondary"
          ellipsis={{ rows: 3 }}
          style={{ margin: 0, color: colors.text.secondary, fontSize: 12 }}
        >
          {insight.detailed_action}
        </Typography.Paragraph>
        <Flex vertical align="start" gap={10}>
          <InsightCategoryTag category={insight.category} />
          <Flex
            gap={10}
            justify="space-between"
            align="center"
            wrap="wrap"
            style={{ width: "100%" }}
          >
            <Typography.Text
              type={label?.type}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                opacity: label ? 1 : 0,
              }}
            >
              {label?.icon}
              {t(`media.analytics.insights.${label?.text}`)}
            </Typography.Text>
            {!isCompleted && !isDismissed && !isPostponed && (
              <InsightActionButtons
                insightId={insight.id ?? ""}
                onComplete={onComplete}
                onPostpone={onPostpone}
                onDismiss={onDismiss}
                onReport={onReport}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
